/**
 * EducationalContent component - Comprehensive educational resources
 * Targets three audience segments with tailored content
 */

import { DollarSign, Target, TrendingUp, BookOpen, Users, Award } from 'lucide-react'
import '../educationalContent.css'

const EducationalContent = () => {
  const audiences = [
    {
      id: 'budget-conscious',
      icon: DollarSign,
      title: 'For Budget-Conscious Individuals',
      subtitle: 'Practical strategies to manage debt on a tight budget',
      color: '#10b981',
      articles: [
        {
          title: 'Understanding Your Debt-to-Income Ratio',
          content: 'Your debt-to-income (DTI) ratio is a critical metric that lenders use to assess your financial health. Calculate it by dividing your monthly debt payments by your gross monthly income. A DTI below 36% is considered healthy, while anything above 43% may indicate financial stress. Use our calculator to see how increasing your payments can improve this ratio over time.'
        },
        {
          title: 'The Minimum Payment Trap',
          content: 'Paying only the minimum on credit cards can extend your debt for decades and cost thousands in interest. For example, a $5,000 balance at 18% APR with a $100 minimum payment would take over 7 years to pay off and cost $3,300 in interest. Increasing your payment to just $150/month cuts the time to 4 years and saves $1,800 in interest.'
        },
        {
          title: 'Smart Budgeting for Debt Repayment',
          content: 'The 50/30/20 budget rule suggests allocating 50% of income to needs, 30% to wants, and 20% to savings and debt repayment. If you\'re in debt, consider temporarily shifting to a 50/20/30 split, dedicating extra funds to accelerated debt payoff. Even small adjustments like reducing dining out by $100/month can significantly impact your debt timeline.'
        },
        {
          title: 'Emergency Fund vs. Debt: Finding Balance',
          content: 'While it\'s tempting to throw all extra money at debt, maintaining a small emergency fund ($500-$1,000) prevents you from accumulating new debt when unexpected expenses arise. Once you have this cushion, focus aggressively on high-interest debt while maintaining minimum payments on everything else.'
        }
      ]
    },
    {
      id: 'goal-oriented',
      icon: Target,
      title: 'For Goal-Oriented Planners',
      subtitle: 'Strategic approaches to accelerate your financial freedom',
      color: '#3b82f6',
      articles: [
        {
          title: 'Debt Avalanche vs. Debt Snowball Methods',
          content: 'The debt avalanche method prioritizes paying off debts with the highest interest rates first, saving the most money over time. The debt snowball method targets smallest balances first, providing psychological wins. For a $10,000 portfolio with mixed rates, avalanche saves approximately $800 more but takes the same time. Choose based on what motivates you.'
        },
        {
          title: 'Setting SMART Debt Payoff Goals',
          content: 'Create Specific, Measurable, Achievable, Relevant, and Time-bound goals. Instead of "pay off debt quickly," try "pay off $5,000 credit card balance in 18 months by paying $300/month." Our calculator helps you test different scenarios to set realistic timelines that align with your income and expenses.'
        },
        {
          title: 'The Power of Bi-Weekly Payments',
          content: 'Switching from monthly to bi-weekly payments (half your monthly amount every two weeks) results in 13 full payments per year instead of 12. On a $10,000 loan at 6% APR, this strategy can shave 6-12 months off your payoff timeline and save hundreds in interest without significantly impacting your budget.'
        },
        {
          title: 'Tracking Progress and Staying Motivated',
          content: 'Create a visual debt payoff tracker using our payment schedule. Mark off each month as you progress, celebrate milestones (25%, 50%, 75% paid), and review your declining principal balance regularly. Studies show that people who track their debt repayment are 42% more likely to achieve their payoff goals.'
        },
        {
          title: 'Windfall Strategy: What to Do with Extra Money',
          content: 'When you receive unexpected money (tax refund, bonus, gift), apply it strategically. If you have high-interest debt (>7%), put the entire windfall toward it. For moderate debt (4-7%), consider splitting 70/30 between debt and emergency savings. Always run the numbers through our calculator to see the impact of lump-sum payments.'
        }
      ]
    },
    {
      id: 'financial-planners',
      icon: TrendingUp,
      title: 'For Financial Planners & Advisors',
      subtitle: 'Advanced insights for comprehensive financial planning',
      color: '#8b5cf6',
      articles: [
        {
          title: 'Amortization Schedules in Client Planning',
          content: 'Understanding amortization is crucial for holistic financial planning. Early in a loan\'s life, most payments go toward interest; later, more reduces principal. This affects tax planning (mortgage interest deductions), refinancing decisions, and payoff strategies. Use detailed amortization schedules to show clients exactly how their money is applied over time.'
        },
        {
          title: 'Tax Implications of Debt Repayment Strategies',
          content: 'Not all debt is created equal from a tax perspective. Mortgage and student loan interest may be deductible, while credit card interest is not. When advising clients, consider the after-tax cost of debt. A 5% mortgage with a 24% tax bracket has an effective rate of 3.8%. This affects whether to prioritize debt payoff over investments with higher returns.'
        },
        {
          title: 'Debt Consolidation: When It Makes Sense',
          content: 'Consolidating multiple high-interest debts into a single lower-rate loan can save thousands and simplify repayment. However, beware of extending the term—a lower payment over longer time may cost more in total interest. Always calculate total interest paid under both scenarios. Our calculator helps model different consolidation scenarios quickly.'
        },
        {
          title: 'Opportunity Cost: Debt Payoff vs. Investing',
          content: 'The mathematical break-even point is when investment returns exceed debt interest rates. With average stock market returns of 10% and high-interest credit card debt at 18%, paying off debt "earns" a guaranteed 18% return. However, for low-rate debt (< 4-5%), investing may yield better long-term wealth accumulation, especially in tax-advantaged accounts.'
        },
        {
          title: 'Behavioral Economics in Debt Management',
          content: 'Understanding client psychology is as important as the math. The debt snowball method (smallest balance first) may be less efficient but yields better compliance rates due to quick wins. Present both avalanche and snowball projections, letting clients choose based on their motivational triggers. Success rates increase 35% when clients select their own strategy.'
        },
        {
          title: 'Credit Score Impact of Repayment Strategies',
          content: 'Aggressive debt payoff positively impacts credit scores through reduced credit utilization (aim for <30% of limits) and payment history (35% of FICO score). However, closing accounts after payoff can hurt credit by reducing available credit and average account age. Advise clients to keep old accounts open with zero balances to maintain optimal credit profiles.'
        }
      ]
    }
  ]

  return (
    <section id="education" className="educational-content-section">
      <div className="section-container">
        <div className="section-header">
          <h2>Educational Resources</h2>
          <p className="section-subtitle">
            Expert guidance tailored to your financial journey—from basic budgeting to advanced planning strategies
          </p>
        </div>

        <div className="audiences-container">
          {audiences.map((audience) => (
            <div key={audience.id} className="audience-section" id={audience.id}>
              <div className="audience-header" style={{ borderColor: audience.color }}>
                <div className="audience-icon" style={{ background: `${audience.color}15`, color: audience.color }}>
                  <audience.icon size={36} />
                </div>
                <div>
                  <h3>{audience.title}</h3>
                  <p>{audience.subtitle}</p>
                </div>
              </div>

              <div className="articles-grid">
                {audience.articles.map((article, index) => (
                  <article key={index} className="article-card">
                    <div className="article-number" style={{ background: audience.color }}>
                      {index + 1}
                    </div>
                    <h4>{article.title}</h4>
                    <p>{article.content}</p>
                  </article>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Additional Resources Section */}
        <div className="resources-callout">
          <h3>Continue Your Financial Education</h3>
          <div className="resources-grid">
            <div className="resource-card">
              <BookOpen size={32} />
              <h4>Recommended Reading</h4>
              <ul>
                <li>"The Total Money Makeover" by Dave Ramsey</li>
                <li>"Your Money or Your Life" by Vicki Robin</li>
                <li>"The Automatic Millionaire" by David Bach</li>
              </ul>
            </div>
            <div className="resource-card">
              <Users size={32} />
              <h4>Community Support</h4>
              <ul>
                <li>Join debt-free communities on Reddit (r/DaveRamsey, r/personalfinance)</li>
                <li>Find accountability partners for your journey</li>
                <li>Share your success story to inspire others</li>
              </ul>
            </div>
            <div className="resource-card">
              <Award size={32} />
              <h4>Professional Help</h4>
              <ul>
                <li>NFCC-certified credit counselors for free advice</li>
                <li>Fee-only financial planners for comprehensive planning</li>
                <li>Legal aid for debt settlement or bankruptcy guidance</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default EducationalContent
